import type { CancelTokenSource } from 'axios';
import { useCallback } from 'react';
import type { DropzoneState } from 'react-dropzone';

import { customPacingUploadCSV } from '../../../../../../apis/campaignGraphqlManager/customPacing';
import type {
  SavedScheduleResult,
  UploadCSVErrorResponse,
} from '../../../../../../apis/campaignGraphqlManager/customPacing/types';
import { openToastAlert } from '../../../../../../common/ToastAlert/toastAlert';
import type { UseFileUploadResponse } from '../../../common/hooks/useFileUpload';
import useFileUpload from '../../../common/hooks/useFileUpload';
import { useRefetchUpdatedLineItems } from '../../hooks/useRefetchUpdatedLineItems';
import { csvFileValidator } from './utils';

export type UseCSVUploadResponse = {
  hovering: boolean;
  percentUploaded?: number;
  loading: boolean;
  cancelRequest: () => void;
  csvAddedFileName: string | null;
  onUploadCSV: () => Promise<void>;
} & Pick<DropzoneState, 'getRootProps' | 'getInputProps'>;

export type Props = {
  onSuccessCSVUpload: (saveScheduleResult: SavedScheduleResult) => void;
};

const useCSVUpload = ({ onSuccessCSVUpload }: Props): UseCSVUploadResponse => {
  const { refetchLineItems } = useRefetchUpdatedLineItems();

  const uploadFile = useCallback(
    async (
      file: File,
      setPercentUploaded: (percent: number) => void,
      cancelTokenSource: CancelTokenSource
    ): Promise<void> => {
      const saveScheduleResult = await customPacingUploadCSV(file, setPercentUploaded, cancelTokenSource);
      await refetchLineItems(saveScheduleResult.lineItemIdList || []);
      onSuccessCSVUpload(saveScheduleResult);
    },
    [refetchLineItems, onSuccessCSVUpload]
  );

  const onError = useCallback((e: unknown): void => {
    const error = e as Pick<UploadCSVErrorResponse, 'name' | 'message'>;

    openToastAlert({
      alertType: 'error',
      message: error.name,
      description: error.message,
    });
  }, []);

  const { fileAddedName, onUpload, ...fileUpload }: UseFileUploadResponse = useFileUpload({
    accept: ['.CSV'],
    validator: csvFileValidator,
    uploadFile,
    onError,
  });

  return {
    ...fileUpload,
    csvAddedFileName: fileAddedName,
    onUploadCSV: onUpload,
  };
};

export default useCSVUpload;
